import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface TimeSlot {
  startTime: string
  endTime: string
}

export interface OfferingTimePreset {
  id: string | number
  restaurantId: string | number
  name: string
  timings: Record<string, TimeSlot[]>
  createdAt?: string
  updatedAt?: string
}

export interface ApiTimingSlot {
  week_day: string
  available_timings: string[]
}

export interface ApiOfferingTimePresetRequest {
  name: string
  timings: ApiTimingSlot[]
}

export interface ApiOfferingTimePresetResponse {
  error: boolean
  message: string
  data: {
    id: string | number
    name: string
    timings: ApiTimingSlot[]
  }
}

export const useOfferingTimePresets = () => {
  const presets = ref<OfferingTimePreset[]>([])
  const selectedRestaurantId = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const setSelectedRestaurantId = (restaurantId: string | null) => {
    selectedRestaurantId.value = restaurantId
  }

  const transformToApiFormat = (timings: Record<string, TimeSlot[]>): ApiTimingSlot[] => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    return days.map(day => ({
      week_day: day,
      available_timings: (timings[day] || []).map(slot => `${slot.startTime}-${slot.endTime}`),
    }))
  }

  const transformFromApiFormat = (apiTimings: ApiTimingSlot[]): Record<string, TimeSlot[]> => {
    const result: Record<string, TimeSlot[]> = {}
    apiTimings.forEach(item => {
      result[item.week_day] = item.available_timings.map(timing => {
        const [startTime, endTime] = timing.split('-')
        return { startTime, endTime }
      })
    })
    return result
  }

  const addPreset = async (preset: Omit<OfferingTimePreset, 'id' | 'createdAt' | 'updatedAt'>) => {
    isLoading.value = true
    error.value = null

    try {
      const apiPayload: ApiOfferingTimePresetRequest = {
        name: preset.name,
        timings: transformToApiFormat(preset.timings),
      }

      const response = await apiInstance.post<ApiOfferingTimePresetResponse>(
        `/restaurant/${preset.restaurantId}/offering-times`,
        apiPayload
      )

      if (response.data.error) {
        const err = new Error(response.data.message) as any
        err.response = { data: response.data }
        throw err
      }

      const newPreset: OfferingTimePreset = {
        id: response.data.data.id,
        restaurantId: preset.restaurantId,
        name: response.data.data.name,
        timings: transformFromApiFormat(response.data.data.timings),
      }

      presets.value.push(newPreset)
      return newPreset
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create offering time preset'
      error.value = errorMessage
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const getPresetsForRestaurant = (restaurantId: string | number) => {
    return presets.value.filter(p => p.restaurantId === restaurantId)
  }

  const getPresetById = (id: string | number) => {
    return presets.value.find(p => p.id === id)
  }

  const updatePreset = async (
    id: string | number,
    restaurantId: string | number,
    updates: Partial<Omit<OfferingTimePreset, 'id' | 'createdAt' | 'restaurantId'>>
  ) => {
    isLoading.value = true
    error.value = null

    try {
      const existingPreset = getPresetById(id)
      if (!existingPreset) {
        throw new Error('Preset not found')
      }

      const updatedTimings = updates.timings || existingPreset.timings
      const apiPayload: ApiOfferingTimePresetRequest = {
        name: updates.name || existingPreset.name,
        timings: transformToApiFormat(updatedTimings),
      }

      const response = await apiInstance.put<ApiOfferingTimePresetResponse>(
        `/restaurant/${restaurantId}/offering-times/${id}`,
        apiPayload
      )

      if (response.data.error) {
        const err = new Error(response.data.message) as any
        err.response = { data: response.data }
        throw err
      }

      const index = presets.value.findIndex(p => p.id === id)
      if (index !== -1) {
        presets.value[index] = {
          ...presets.value[index],
          name: response.data.data.name,
          timings: transformFromApiFormat(response.data.data.timings),
        }
        return presets.value[index]
      }

      return null
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update offering time preset'
      error.value = errorMessage
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const deletePreset = async (id: string | number, restaurantId: string | number) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiInstance.delete<{
        error: boolean
        message: string
        data: any
      }>(`/restaurant/${restaurantId}/offering-times/${id}`)

      if (response.data.error) {
        const err = new Error(response.data.message) as any
        err.response = { data: response.data }
        throw err
      }

      presets.value = presets.value.filter(p => p.id !== id)
      return true
    } catch (err: any) {
      let errorMessage = 'Failed to delete offering time preset'

      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err instanceof Error) {
        errorMessage = err.message
      }

      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  const loadPresetsForRestaurant = async (restaurantId: string | number) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiInstance.get<{
        error: boolean
        message: string
        data: Array<{
          id: string | number
          name: string
          timings: ApiTimingSlot[]
        }>
      }>(`/restaurant/${restaurantId}/offering-times`)

      if (response.data.error) {
        throw new Error(response.data.message)
      }

      const loadedPresets = response.data.data.map(item => ({
        id: item.id,
        restaurantId,
        name: item.name,
        timings: transformFromApiFormat(item.timings),
      }))

      presets.value = loadedPresets
      return loadedPresets
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load offering time presets'
      error.value = errorMessage
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    presets,
    selectedRestaurantId,
    isLoading,
    error,

    // Methods
    setSelectedRestaurantId,
    addPreset,
    getPresetsForRestaurant,
    getPresetById,
    updatePreset,
    deletePreset,
    loadPresetsForRestaurant,
  }
}
